import { useEffect, useRef, useState } from 'react'
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'

export type TranscriptionStatus = 'starting' | 'active' | 'reconnecting' | 'unavailable'

export interface SpeechTokenResult {
  token: string
  region: string
  // Every value below is server-configured (core/config.py's
  // LIVE_TRANSCRIPTION_*/AZURE_SPEECH_TOKEN_LIFETIME_SECONDS), not hardcoded
  // here — see mint_speech_token's own docstring for why. Optional only so
  // this hook still degrades safely (via DEFAULT_TUNING below) against an
  // older cached response shape; a normal response always has them.
  expires_in_seconds?: number
  token_refresh_seconds?: number
  reconnect_burst_base_delay_ms?: number
  reconnect_burst_max_attempts?: number
  long_retry_interval_seconds?: number
}

interface UseLiveTranscriptionOptions {
  role: 'recruiter' | 'candidate'
  mediaStream: MediaStream | null
  enabled: boolean
  sendAppMessage: (message: Record<string, any>) => void
  getToken: () => Promise<SpeechTokenResult>
  // AI-interviewer mode only (LiveInterviewRoomPage's AI branch) — fired with
  // each FINAL recognized utterance, same moment/same text the
  // 'transcript-segment' app message below carries, just as a direct
  // callback instead of a broadcast: there's no recruiter peer to broadcast
  // to in that mode, but the AI orchestrator still needs to know what the
  // candidate just said. Optional and additive — every existing caller that
  // omits it is completely unaffected.
  onRecognized?: (text: string) => void
  // AI-interviewer mode only. While true, the microphone's audio is
  // SILENCED before it ever reaches Azure's recognizer (via a Web Audio gain
  // node - see the graph built in startRecognition below), not just filtered
  // after the fact. This exists because the candidate's OWN microphone picks
  // up the AI's synthesized voice playing through their speakers - regardless
  // of whether their mic button shows on or off - and Azure's STT has no way
  // to tell that apart from the candidate's own speech. Gating at the SOURCE
  // (rather than dropping the recognized/recognizing event after the fact)
  // matters because Azure's STT finalizes a result a meaningful moment AFTER
  // the audio that produced it - for a long utterance this lag can run
  // several seconds, long enough to outlast a short "is the AI still
  // speaking" event-level check. Zeroing the gain means there is nothing of
  // the AI's own voice for the recognizer to ever buffer or finalize in the
  // first place, regardless of how long its own processing takes. The
  // caller is expected to keep this true for the AI's ENTIRE speaking
  // duration plus a trailing safety window sized to the length of what was
  // said.
  suppressed?: boolean
  // AI-interviewer mode only: the AI's own most recently spoken line. A
  // second, independent safety net alongside `suppressed` for the SAME
  // acoustic-leak problem - if a recognized result is overwhelmingly a
  // contiguous fragment of this text, it is almost certainly the candidate's
  // mic picking up the AI's own synthesized voice, not something the
  // candidate said, and is dropped before it's ever broadcast as a
  // transcript segment or handed to onRecognized. This exists because the
  // audio-level gate (`suppressed`) is timing-sensitive - it cannot predict
  // exactly how long the browser's TTS engine will take to finish playing -
  // and has, in practice, still let fragments through; this check has no
  // timing dependency at all, so it still catches an echo even when the
  // audio gate's timing races. The backend applies the identical check
  // server-side too (ai_interviewer.py's _is_echo_of_ai) as the primary,
  // unconditional guarantee for the AI's own conversation logic - this is
  // purely so the candidate-facing transcript panel doesn't show it either.
  lastAiUtterance?: string | null
}

function normalizeForEchoCheck(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim()
}

function looksLikeAiEcho(candidateText: string, lastAiUtterance: string | null | undefined): boolean {
  if (!lastAiUtterance) return false
  const candidate = normalizeForEchoCheck(candidateText)
  const ai = normalizeForEchoCheck(lastAiUtterance)
  // Short replies are never treated as echo — mirrors the backend's
  // _ECHO_MIN_WORDS. The AI's own lines are full of "ready to begin" /
  // "start the interview", so fuzzy-matching a 3-4 word "I'm ready to
  // begin" used to swallow the candidate's real answer and the interview
  // never started. A genuine leak is a long run of the AI's sentence.
  if (candidate.split(' ').length < 6 || !ai) return false
  if (ai.includes(candidate)) return true
  // Longest contiguous run of the candidate text found anywhere in the AI's
  // line, relative to the candidate text's own length - catches a partially
  // garbled leak (STT mis-hearing a word or two of its own TTS output)
  // without needing an exact substring match. Only runs when the cheap
  // substring check above misses.
  const minLen = Math.ceil(candidate.length * 0.7)
  for (let len = candidate.length; len >= minLen; len--) {
    for (let i = 0; i + len <= candidate.length; i++) {
      if (ai.includes(candidate.slice(i, i + len))) return true
    }
  }
  return false
}

// Used only until the first real token response arrives (or if a response
// is ever missing a field) — matches this feature's original hardcoded
// values, kept here purely as a safety net, never as the source of truth.
const DEFAULT_TUNING = {
  tokenRefreshMs: 8 * 60 * 1000,
  reconnectBurstBaseDelayMs: 2000,
  reconnectBurstMaxAttempts: 5,
  longRetryIntervalMs: 60 * 1000,
}

/**
 * Real-time, speaker-attributed live transcription for a Live Interview
 * call — each participant's OWN microphone streams directly from their own
 * browser to Azure AI Speech's real-time STT (never the browser's native
 * SpeechRecognition, which is what useDictation.ts uses for a completely
 * unrelated feature — the two share no engine, no MediaStream object, no
 * mutual exclusion is needed). Because each stream is provably one
 * person's own mic, `role` is attached here and trusted by the server as
 * this connection's own resolved identity — there is no diarization, no
 * cross-referencing against a volume timeline.
 *
 * This is the SECOND attempt at live transcription in this codebase. The
 * first used the browser's native Web Speech API and was fully reverted
 * after it repeatedly, silently stopped delivering results in Chrome with
 * no error event at all. Azure's SDK reliably fires `canceled` on any
 * connection loss instead — this hook's whole reliability contract below
 * exists specifically because that event is trustworthy in a way the old
 * approach's silence never was.
 *
 * Degrades gracefully, always: a token-mint failure, a `canceled` event
 * that exhausts its retries, or an unsupported browser all land on
 * `'unavailable'` — never an exception that escapes this hook. This hook
 * reads `mediaStream` and calls `sendAppMessage`, nothing else; it has zero
 * coupling to the call's own WebRTC/media state, so nothing it does can
 * affect video, audio, chat, or scoring.
 *
 * All timing below (token refresh cadence, reconnect burst/backoff, the
 * long-term retry interval) comes from the backend's config, not hardcoded
 * constants — see SpeechTokenResult's own docstring. An interview can run
 * for hours, and these are exactly the knobs an operator may need to tune
 * without a frontend redeploy.
 */
export default function useLiveTranscription({
  role, mediaStream, enabled, sendAppMessage, getToken, onRecognized, suppressed, lastAiUtterance,
}: UseLiveTranscriptionOptions) {
  const [transcriptionStatus, setTranscriptionStatus] = useState<TranscriptionStatus>('starting')
  const [interimText, setInterimText] = useState('')

  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const stoppedRef = useRef(false)
  const sendAppMessageRef = useRef(sendAppMessage)
  sendAppMessageRef.current = sendAppMessage
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const onRecognizedRef = useRef(onRecognized)
  onRecognizedRef.current = onRecognized
  const lastAiUtteranceRef = useRef(lastAiUtterance)
  lastAiUtteranceRef.current = lastAiUtterance
  // The actual gate - a Web Audio GainNode sitting between the raw mic track
  // and whatever Azure's recognizer reads from (see startRecognition below).
  // Written directly here (not through React state) so toggling it is
  // instantaneous and never waits on a render.
  const gainNodeRef = useRef<GainNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  useEffect(() => {
    if (gainNodeRef.current) gainNodeRef.current.gain.value = suppressed ? 0 : 1
  }, [suppressed])
  // Latest server-configured tuning — updated every time a token is minted,
  // read by the reconnect/refresh logic below. Starts at DEFAULT_TUNING and
  // is immediately overwritten by the first real response.
  const tuningRef = useRef({ ...DEFAULT_TUNING })

  useEffect(() => {
    if (!enabled || !mediaStream) {
      setTranscriptionStatus('starting')
      return
    }

    stoppedRef.current = false
    let cancelled = false

    const applyTuning = (result: SpeechTokenResult) => {
      tuningRef.current = {
        tokenRefreshMs: (result.token_refresh_seconds ?? DEFAULT_TUNING.tokenRefreshMs / 1000) * 1000,
        reconnectBurstBaseDelayMs: result.reconnect_burst_base_delay_ms ?? DEFAULT_TUNING.reconnectBurstBaseDelayMs,
        reconnectBurstMaxAttempts: result.reconnect_burst_max_attempts ?? DEFAULT_TUNING.reconnectBurstMaxAttempts,
        longRetryIntervalMs: (result.long_retry_interval_seconds ?? DEFAULT_TUNING.longRetryIntervalMs / 1000) * 1000,
      }
    }

    const teardown = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
      const recognizer = recognizerRef.current
      recognizerRef.current = null
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync(
          () => recognizer.close(),
          () => recognizer.close(),
        )
      }
      gainNodeRef.current = null
      const audioContext = audioContextRef.current
      audioContextRef.current = null
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => { /* already closing/closed - fine */ })
      }
    }

    const scheduleTokenRefresh = () => {
      refreshTimerRef.current = setTimeout(async () => {
        if (cancelled || !recognizerRef.current) return
        try {
          const result = await getTokenRef.current()
          if (cancelled || !recognizerRef.current) return
          applyTuning(result)
          // The SDK's own supported pattern for rotating credentials on a
          // long-running recognizer without tearing it down.
          recognizerRef.current.authorizationToken = result.token
          scheduleTokenRefresh()
        } catch {
          // A failed refresh doesn't tear down an otherwise-working
          // recognizer — it will simply fail its own way once Azure
          // actually rejects the stale token, which surfaces as a
          // `canceled` event handled by the same reconnect path below.
          // Retry the refresh itself on the same cadence rather than giving
          // up on refreshing forever after one transient failure.
          scheduleTokenRefresh()
        }
      }, tuningRef.current.tokenRefreshMs)
    }

    const startRecognition = async () => {
      try {
        const result = await getTokenRef.current()
        if (cancelled) return
        applyTuning(result)
        const { token, region } = result

        // fromAuthorizationToken(token, region), NOT fromHost(endpoint):
        // confirmed via a live test that this resource's custom-subdomain
        // endpoint mints tokens fine but flatly refuses the real-time
        // recognition WebSocket (close code 1006) — that connection is a
        // region-only capability (wss://<region>.stt.speech.microsoft.com/...),
        // served from a different place than the REST/token-issuance host.
        const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region)
        speechConfig.speechRecognitionLanguage = 'en-US'

        // Gate the mic through a GainNode BEFORE Azure ever sees it, rather
        // than handing the raw mediaStream straight to AudioConfig - see
        // `suppressed`'s own doc comment on UseLiveTranscriptionOptions for
        // why this has to happen at the source. A fresh AudioContext/graph
        // per recognizer start (not shared/reused) keeps this self-contained
        // and torn down cleanly alongside the recognizer in teardown().
        const audioContext = new AudioContext()
        const source = audioContext.createMediaStreamSource(mediaStream)
        const gainNode = audioContext.createGain()
        gainNode.gain.value = suppressed ? 0 : 1
        const destination = audioContext.createMediaStreamDestination()
        source.connect(gainNode).connect(destination)
        audioContextRef.current = audioContext
        gainNodeRef.current = gainNode

        const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(destination.stream)
        const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)
        recognizerRef.current = recognizer

        recognizer.sessionStarted = () => {
          // The GENUINE success signal: the WebSocket to Azure actually
          // connected and a recognition session is live. This is deliberately
          // NOT the same moment startContinuousRecognitionAsync's own success
          // callback fires below — that only confirms the SDK locally
          // accepted the start command, well before the underlying network
          // connection is verified. Resetting the retry counter there instead
          // of here was the bug: a connection that starts locally then fails
          // moments later (surfaced via `canceled`) never accumulated toward
          // the burst cap, so it retried forever instead of ever backing off.
          if (cancelled) return
          reconnectAttemptsRef.current = 0
          setTranscriptionStatus('active')
        }

        // Belt-and-suspenders on top of the gain node above: reads the gate's
        // CURRENT value directly (not a stale closure) in case a sliver of
        // audio from right at a gate transition still produces a result.
        const isGateClosed = () => (gainNodeRef.current?.gain.value ?? 1) === 0

        recognizer.recognizing = (_sender, event) => {
          if (isGateClosed()) return
          setInterimText(event.result.text || '')
        }

        recognizer.recognized = (_sender, event) => {
          setInterimText('')
          if (isGateClosed()) return
          if (event.result.reason !== SpeechSDK.ResultReason.RecognizedSpeech) return
          const text = (event.result.text || '').trim()
          if (!text) return
          // Second, timing-independent safety net alongside the gain gate
          // above — see lastAiUtterance's own doc comment.
          if (looksLikeAiEcho(text, lastAiUtteranceRef.current)) return
          // offset/duration are in 100-ns ticks, relative to when THIS
          // recognizer's own session started — each side anchors
          // independently at its own call-connected moment. Sub-second
          // cross-participant skew is acceptable for this UI; this is not
          // a protocol-level synchronized clock.
          const startSeconds = event.result.offset / 10_000_000
          const endSeconds = startSeconds + event.result.duration / 10_000_000
          sendAppMessageRef.current({
            type: 'transcript-segment',
            start_seconds: startSeconds,
            end_seconds: endSeconds,
            text,
          })
          onRecognizedRef.current?.(text)
        }

        recognizer.canceled = (_sender, event) => {
          setInterimText('')
          if (event.reason === SpeechSDK.CancellationReason.Error) {
            // eslint-disable-next-line no-console
            console.error(
              'Live transcription: Azure Speech connection canceled —',
              'errorCode:', event.errorCode, 'errorDetails:', event.errorDetails,
            )
          }
          if (stoppedRef.current || cancelled) return
          if (event.reason === SpeechSDK.CancellationReason.Error) {
            attemptReconnect()
          }
        }

        recognizer.sessionStopped = () => {
          setInterimText('')
        }

        await new Promise<void>((resolve, reject) => {
          recognizer.startContinuousRecognitionAsync(() => resolve(), (err) => reject(err))
        })
        if (cancelled) return
        // Deliberately NOT resetting the retry counter or setting 'active'
        // here — this only confirms the SDK locally accepted the start
        // command, not that Azure actually accepted the connection. See
        // recognizer.sessionStarted above for the genuine signal.
        scheduleTokenRefresh()
      } catch {
        if (!cancelled) attemptReconnect()
      }
    }

    // Reconnect strategy: a short, fast burst first (the common case — a
    // brief blip, or Azure recycling the connection at its own session cap
    // per the org's own Speech reference doc), THEN — if the burst doesn't
    // recover — an indefinite slow retry, never a permanent give-up. An
    // interview can run for hours; a transient outage partway through must
    // not silently disable transcription for however much of the call
    // remains. 'unavailable' is therefore always a TEMPORARY status here,
    // re-attempted on tuningRef.current.longRetryIntervalMs, for as long as
    // this hook stays mounted.
    const attemptReconnect = () => {
      if (cancelled || stoppedRef.current) return
      teardown()
      reconnectAttemptsRef.current += 1
      const { reconnectBurstMaxAttempts, reconnectBurstBaseDelayMs, longRetryIntervalMs } = tuningRef.current
      if (reconnectAttemptsRef.current > reconnectBurstMaxAttempts) {
        setTranscriptionStatus('unavailable')
        setTimeout(() => {
          if (!cancelled) void startRecognition()
        }, longRetryIntervalMs)
        return
      }
      setTranscriptionStatus('reconnecting')
      const delay = reconnectBurstBaseDelayMs * reconnectAttemptsRef.current
      setTimeout(() => {
        if (!cancelled) void startRecognition()
      }, delay)
    }

    void startRecognition()

    return () => {
      cancelled = true
      stoppedRef.current = true
      teardown()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mediaStream, role])

  return { transcriptionStatus, interimText }
}
